// resizeImage.jsx
import { DeleteOutlined } from "@ant-design/icons";
import { Button, Image, Tooltip, message } from "antd";
import { useEffect, useRef, useState } from "react";
import FileResizer from "react-image-file-resizer";
import { fileToBase64, isEmpty } from "../../../tools/tools";

const ResizeImage = ({
	exportImage,
	value = "",
	disabled = false,
	idelem = new Date().getTime(),
	style = {},
	formats, // مثل ["jpg"] یا ["image/jpeg"] اگر ندی، فرمت ورودی حفظ می‌شود
}) => {
	const [tempImage, setTempImage] = useState();
	const fileInputRef = useRef(null);
	const [disabledImage, setDisabledImage] = useState(false);

	useEffect(() => {
		setDisabledImage(disabled);
	}, [disabled]);

	useEffect(() => {
		const callIntialImage = async () => {
			const tempImg = await fileToBase64(value);
			setTempImage(tempImg);
		};
		if (value && (value?.size > 0 || typeof value === "string")) callIntialImage();
	}, [value]);

	/* ===================== helpers ===================== */

	const TARGET_KB = parseInt(process.env.REACT_APP_MAX_SIZE_IMAGE || "500", 10);
	const TARGET_BYTES = TARGET_KB * 1024;

	const mimeFromExt = (ext) => {
		const e = ext.toLowerCase().replace(".", "");
		if (e === "jpg" || e === "jpeg") return "image/jpeg";
		if (e === "png") return "image/png";
		if (e === "webp") return "image/webp";
		if (e === "gif") return "image/gif";
		if (e === "bmp") return "image/bmp";
		if (e === "heic" || e === "heif") return "image/heic";
		return "";
	};

	const getExtFromName = (name = "") => {
		const m = String(name).match(/\.([a-z0-9]+)$/i);
		return m ? m[1].toLowerCase() : "";
	};

	const getFileType = (file) => {
		const fallbackMime = mimeFromExt(getExtFromName(file.name));
		const type = file.type || fallbackMime;
		return { mime: type, ext: getExtFromName(file.name) };
	};

	const isImageMime = (mime = "") => mime && mime.startsWith("image/");

	const normalizeFormats = (arr) =>
		(arr || []).map((f) => (String(f).startsWith("image/") ? String(f).toLowerCase() : mimeFromExt(String(f)))).filter(Boolean);

	// ریسایز با حفظ همان فرمت ورودی
	function resizeOnceSameFormat({ file, maxWidth, maxHeight, mime, quality }) {
		let compressFormat = "JPEG";
		if (mime === "image/png") compressFormat = "PNG";
		else if (mime === "image/webp") compressFormat = "WEBP";
		else if (mime === "image/gif") compressFormat = "GIF"; // برای GIF انیمیشن از بین می‌رود، بهتره دست نزنیم
		else if (mime === "image/jpeg") compressFormat = "JPEG";

		return new Promise((resolve, reject) => {
			try {
				FileResizer.imageFileResizer(
					file,
					maxWidth,
					maxHeight,
					compressFormat,
					quality, // برای PNG/GIF بی‌اثر است
					0,
					(resized) => resolve(resized),
					"file",
				);
			} catch (e) {
				reject(e);
			}
		});
	}

	// جست‌وجوی دودویی کیفیت برای JPEG/WEBP
	async function findQualityForTargetSameFormat({ file, maxWidth, maxHeight, mime, targetBytes, lo = 60, hi = 95 }) {
		let best = null;
		for (let i = 0; i < 7; i++) {
			const mid = Math.round((lo + hi) / 2);
			const out = await resizeOnceSameFormat({ file, maxWidth, maxHeight, mime, quality: mid });
			if (out.size <= targetBytes) {
				best = out;
				lo = mid + 1; // کیفیت بالاتر شاید هنوز جا شود
			} else {
				hi = mid - 1; // باید کیفیت کم‌تر شود
			}
		}
		return best;
	}

	/**
	 * strictConstrainImageSameFormat
	 * فرمت تغییر نمی‌کند. حتماً ≤ TARGET_BYTES خواهد بود یا throw می‌کند.
	 */
	async function strictConstrainImageSameFormat({
		file,
		targetBytes = TARGET_BYTES,
		startMaxWidth = 1500,
		startMaxHeight = 1500,
		minQuality = 60,
		minWidth = 64,
		minHeight = 64,
	}) {
		const { mime } = getFileType(file);

		// باید تصویر باشد
		if (!isImageMime(mime)) throw new Error("NOT_IMAGE");

		// اگر همین الان زیر سقف است، قبول
		if (file.size <= targetBytes) return file;

		// GIF: بدون تغییر فرمت عملاً نمی‌شود بهینه کرد
		if (mime === "image/gif") throw new Error("GIF_TOO_LARGE");

		let w = startMaxWidth;
		let h = startMaxHeight;

		// حداکثر 8 دور: هر دور تلاش برای رسیدن به target
		for (let round = 0; round < 8; round++) {
			let candidate = null;

			if (mime === "image/jpeg" || mime === "image/webp") {
				// کیفیت با دودویی
				let lo = minQuality;
				let hi = 95;
				const sizeRatio = file.size / targetBytes;
				if (sizeRatio > 6) hi = 85;
				if (sizeRatio > 10) hi = 80;

				candidate = await findQualityForTargetSameFormat({
					file,
					maxWidth: w,
					maxHeight: h,
					mime,
					targetBytes,
					lo,
					hi,
				});

				// اگر هنوز بزرگ بود، یک تلاش مستقیم با حداقل کیفیت
				if (!candidate) {
					candidate = await resizeOnceSameFormat({ file, maxWidth: w, maxHeight: h, mime, quality: minQuality });
				}
			} else {
				// PNG و دیگر lossless ها: فقط ابعاد را کم می‌کنیم
				candidate = await resizeOnceSameFormat({ file, maxWidth: w, maxHeight: h, mime, quality: 90 });
			}

			if (candidate && candidate.size <= targetBytes) return candidate;

			// کاهش ابعاد برای تلاش بعدی
			const scale = 0.75; // 25% کاهش
			const nextW = Math.max(minWidth, Math.floor(w * scale));
			const nextH = Math.max(minHeight, Math.floor(h * scale));

			if (nextW === w && nextH === h) break; // دیگر جایی برای کوچک‌تر شدن نیست

			w = nextW;
			h = nextH;
		}

		// نرسیدیم: بدون تغییر فرمت نمی‌شود
		throw new Error("UNABLE_TO_MEET_TARGET");
	}

	/* ============================ handlers ============================ */

	const handleChangeFileCert = async (e) => {
		try {
			const selected = e.target.files?.[0];
			if (!selected) return;

			// تشخیص MIME ورودی
			const { mime } = getFileType(selected);
			if (!isImageMime(mime)) {
				message.error("فایل انتخاب‌شده تصویر نیست.");
				return;
			}

			// اگر formats مشخص شده، باید دقیقاً همان باشد
			const allowedMimes = normalizeFormats(formats);
			if (allowedMimes.length && !allowedMimes.includes(mime)) {
				message.error("فرمت فایل مجاز نیست.");
				return;
			}

			// خروجی باید قطعاً ≤ 500KB باشد، و فرمت همان ورودی
			const constrained = await strictConstrainImageSameFormat({
				file: selected,
				targetBytes: TARGET_BYTES,
				startMaxWidth: 1500,
				startMaxHeight: 1500,
				minQuality: 60,
				minWidth: 64,
				minHeight: 64,
			});

			// تضمین دوباره
			if (constrained.size > TARGET_BYTES) {
				message.error("حجم تصویر بعد از بهینه‌سازی هنوز بالای حد مجاز است.");
				return;
			}

			const tempImg = await fileToBase64(constrained);
			setTempImage(tempImg);
			exportImage(constrained);
		} catch (err) {
			if (err?.message === "GIF_TOO_LARGE") {
				message.error("این GIF بدون تغییر فرمت قابل رساندن به زیر ۵۰۰KB نیست.");
				return;
			}
			if (err?.message === "UNABLE_TO_MEET_TARGET") {
				message.error("این تصویر بدون تغییر فرمت به زیر ۵۰۰KB نمی‌رسد. لطفاً تصویر کوچک‌تر یا با فرمت مناسب‌تر انتخاب کنید.");
				return;
			}
			if (err?.message === "NOT_IMAGE") {
				message.error("فایل تصویر نیست.");
				return;
			}
			console.error(err);
			message.error("خطا در پردازش تصویر.");
		}
	};

	const handleClear = () => {
		setTempImage("");
		exportImage && exportImage(null);
	};

	// accept بر اساس formats یا همه تصاویر
	const acceptAttr = formats?.length
		? formats.map((f) => (String(f).startsWith("image/") ? f : `image/${String(f).replace(".", "")}`)).join(",")
		: "image/*";

	return (
		<div className={`uploadDocument ${disabledImage ? "disabled" : ""}`} style={style}>
			{isEmpty(tempImage) ? (
				<>
					<label className="lbl-overly" htmlFor={`fileInput${idelem}`}>
						<input
							ref={fileInputRef}
							type="file"
							id={`fileInput${idelem}`}
							disabled={disabledImage}
							accept={acceptAttr}
							onChange={handleChangeFileCert}
						/>
					</label>
					<div className="ant-upload ant-input ">
						<div className="holder-upload-document">
							<p className="ant-upload-hint">
								<span>تصویر مربوطه را تنها با فرمت‌های مجاز بارگذاری نمایید</span>
							</p>
							<label className="lbl-btn">انتخاب فایل</label>
						</div>
					</div>
				</>
			) : (
				<Image
					src={tempImage}
					preview={{
						mask: (
							<div className="holder-action-img">
								<Tooltip title="حذف">
									<Button className="btn-normal" onClick={handleClear}>
										<DeleteOutlined />
									</Button>
								</Tooltip>
							</div>
						),
					}}
				/>
			)}
		</div>
	);
};

export default ResizeImage;
